import React, { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import { twJoin, twMerge } from 'tailwind-merge';

import { CloseIcon, InfoCircle } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { FormInput } from '../../../../../../components/input/index';
import Loader from '../../../../../../components/loader/index';
import { QueryKey } from '../../../../../../constants/index';
import { ScenarioHooks, useDebounce, useInvalidateQueries } from '../../../../../../hooks/index';
import { CreateCustomSafe } from '../../../../../../types/scenario-modeling.types';
import { toRound } from '../../../../../../utils/get-rounded-number.util';
import { toNumber } from '../../../../../../utils/to-number.util';
import { useScenarioCapTableStore } from '../../../../cap-table.store';
import { useScenarioStore } from '../../../../scenario-modeling.store';
import { customSafesFormSchema, FundraisingFormSchema } from '../../../fundraising-validation';
import { CapTypeCombobox, StakeholderCombobox, TableHeaderItem } from '../form-components/index';
import { ShareClassCombobox } from '../form-components/share-class/combobox';
import { NewItemListProps } from './items.types';

export type CustomSafeItemProps = {
  companyId: string;
  control: Control<FundraisingFormSchema>;
  index: number;
  onRemoveItem: () => void;
  scenarioId: string;
  setIsPending: (isPending: boolean) => void;
  sharePrice: number;
  skipUpdateIdsRef: React.MutableRefObject<Set<string>>;
};

const defaultNumericValue = 0.00000000001;

const customSafeGridTemplateColumns = `180px repeat(2, minmax(160px, 1fr)) minmax(150px, 1fr) repeat(2, minmax(120px, 1fr)) 180px 160px repeat(2, minmax(180px, 1fr)) 48px`;

/** A cap that is really entered (the blank placeholder is `defaultNumericValue`, well below 1). */
const hasRealCap = (cap: number | undefined) => Number(cap) >= 1;

export const CustomSafeItem: FC<CustomSafeItemProps> = ({
  control,
  index,
  companyId,
  scenarioId,
  onRemoveItem,
  skipUpdateIdsRef,
  setIsPending,
  sharePrice,
}) => {
  const { customSafes } = useWatch<FundraisingFormSchema>({ control });
  const { success: safeSuccess, error } = customSafesFormSchema
    .superRefine((data, ctx) => {
      data.safeItems.forEach((item, index) => {
        if (item.investment < sharePrice) {
          ctx.addIssue({
            code: 'custom',
            message: 'Investment must be more or equal share price',
            path: ['safeItems', index, 'investment'],
          });
        }
      });
    })
    .safeParse(customSafes);

  const { pointerBlockedStyles } = useScenarioStore();

  const safeItem = customSafes?.safeItems?.[index];
  const lastSentRef = useRef<CreateCustomSafe | null>(null);
  const { scheduleUpdate } = useScenarioCapTableStore();
  const { createCustomSafe, isPending } = ScenarioHooks.useCreateCustomSafe();
  const { updateCustomSafe, isPending: isUpdatePending } = ScenarioHooks.usePatchCustomSafe();
  const { deleteCustomSafe } = ScenarioHooks.useDeleteCustomSafe();

  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_SCENARIO_CUSTOM_SAFES,
    QueryKey.CALC_CUSTOM_SAFE,
    QueryKey.GET_SCENARIO,
  );

  const { invalidateQuery: invalidateSummary } = useInvalidateQueries(
    QueryKey.GET_CONVERSION_SUMMARY,
    QueryKey.CALC_CUSTOM_SAFE,
  );

  const { data } = ScenarioHooks.useCalcCustomSafe({
    scenarioId,
    safeId: safeItem?.id || '',
  });

  const handleRemoveItem = () => {
    if (safeItem?.id) {
      skipUpdateIdsRef.current.add(safeItem.id);
      scheduleUpdate(scenarioId);
      return deleteCustomSafe(
        { safeId: safeItem.id, scenarioId },
        {
          onSuccess: () => {
            onRemoveItem();
            lastSentRef.current = null;
            invalidateQuery();

            setTimeout(() => {
              if (safeItem?.id) {
                skipUpdateIdsRef.current.delete(safeItem.id);
              }
            }, 1000);
          },
        },
      );
    }
    onRemoveItem();
    lastSentRef.current = null;
  };

  const extractSafeSnapshot = useCallback(
    (item: typeof safeItem) => ({
      investment: toNumber(item?.investment),
      shareClassId: item?.shareClass?.id || '',
      cap: toNumber(item?.cap),
      capType: hasRealCap(item?.cap) ? (item?.capType ?? null) : null,
      discount: toNumber(item?.discount),
      floor: toNumber(item?.floor),
      stakeholderId: item?.stakeholder?.id === 'new' ? undefined : item?.stakeholder?.id,
      fakeStakeholderName: item?.stakeholder?.id === 'new' ? item?.stakeholder.fullName : undefined,
    }),
    [],
  );

  const { debounceCallback } = useDebounce<typeof safeItem>({
    debounce: 500,
    callback: (item) => {
      if (!item || !safeSuccess) return;
      if (item.id && skipUpdateIdsRef.current.has(item.id)) return;

      const safeSnapshot = extractSafeSnapshot(item);
      const { id, shareClass, investment, stakeholder, cap, discount, floor } = item;
      const capType = safeSnapshot.capType;

      if (JSON.stringify(safeSnapshot) === JSON.stringify(lastSentRef.current)) return;

      // "Update Scenario" lights up only once the change is accepted: a refused one (e.g. the
      // SAFEs would own 100 % or more) leaves the scenario as it was.
      if (!id) {
        createCustomSafe(
          {
            scenarioId,
            data: {
              investment: toNumber(investment),
              shareClassId: shareClass?.id || '',
              cap: cap === defaultNumericValue ? 0 : toNumber(cap),
              capType,
              discount: discount === defaultNumericValue ? 0 : Math.min(toNumber(discount), 100),
              floor: toNumber(floor),
              stakeholderId: stakeholder?.id === 'new' ? undefined : stakeholder?.id,
              fakeStakeholderName: stakeholder?.id === 'new' ? stakeholder.fullName : undefined,
            },
          },
          {
            onSuccess: () => {
              scheduleUpdate(scenarioId);
              invalidateQuery();
            },
          },
        );
      } else {
        updateCustomSafe(
          {
            scenarioId,
            safeId: id,
            data: {
              investment: toNumber(investment),
              shareClassId: shareClass?.id || '',
              cap: cap === defaultNumericValue ? 0 : toNumber(cap),
              capType,
              discount: discount === defaultNumericValue ? 0 : Math.min(toNumber(discount), 100),
              floor: toNumber(floor),
              stakeholderId: stakeholder?.id === 'new' ? undefined : stakeholder?.id,
              fakeStakeholderName: stakeholder?.id === 'new' ? stakeholder.fullName : undefined,
            },
          },
          {
            onSuccess: () => {
              scheduleUpdate(scenarioId);
              invalidateSummary();
            },
          },
        );
      }
      lastSentRef.current = safeSnapshot;
    },
  });

  useEffect(() => {
    if (!safeItem || (safeItem?.id && skipUpdateIdsRef.current.has(safeItem?.id))) return;

    const currentSnapshot = extractSafeSnapshot(safeItem);
    if (lastSentRef.current === null) {
      lastSentRef.current = currentSnapshot;
      return;
    }

    if (JSON.stringify(currentSnapshot) === JSON.stringify(lastSentRef.current)) return;

    debounceCallback(safeItem);
  }, [debounceCallback, skipUpdateIdsRef, safeItem, safeSuccess, extractSafeSnapshot]);

  useEffect(() => {
    if (isPending || isUpdatePending) {
      setIsPending(true);
    } else {
      const timeout = setTimeout(() => {
        setIsPending(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isPending, isUpdatePending, setIsPending]);

  return (
    <>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <StakeholderCombobox
          companyId={companyId}
          control={control}
          fieldName={`customSafes.safeItems.${index}.stakeholder`}
          scenarioId={scenarioId}
        />
      </div>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <FormInput
          control={control}
          currencySign
          errorMessage={
            customSafes?.safeItems?.[index]?.investment &&
            error?.issues.find(
              (e) => JSON.stringify(e.path) === JSON.stringify(['safeItems', index, 'investment']),
            )?.message
          }
          inputClassName="h-10"
          maxDecimalPlaces={2}
          name={`customSafes.safeItems.${index}.investment`}
          numberOnly
          shouldFormatNumber
        />
      </div>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <FormInput
          control={control}
          inputClassName="h-10"
          name={`customSafes.safeItems.${index}.cap`}
          numberOnly
          shouldFormatNumber
          transform={(value?: number) =>
            value !== undefined && value === defaultNumericValue ? '' : value?.toString()
          }
        />
      </div>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <CapTypeCombobox control={control} index={index} />
      </div>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <FormInput
          control={control}
          inputClassName="h-10"
          name={`customSafes.safeItems.${index}.discount`}
          signAfterValue="%"
          transform={(value?: number) =>
            value !== undefined && value === defaultNumericValue ? '' : value?.toString()
          }
        />
      </div>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <FormInput
          control={control}
          inputClassName="h-10"
          name={`customSafes.safeItems.${index}.floor`}
          numberOnly
          shouldFormatNumber
        />
      </div>
      <div className={twJoin('flex items-center px-2 py-4', pointerBlockedStyles)}>
        <ShareClassCombobox
          companyId={companyId}
          control={control}
          name={`customSafes.safeItems.${index}.shareClass`}
        />
      </div>
      <div className="flex w-full items-center justify-center px-2 py-4">
        <span className="w-full rounded-t-[4px] bg-gray-50 px-3 py-[10px] text-sm font-[450] text-gray-700">
          {data?.sharesConverted ? data?.sharesConverted?.toLocaleString('en-US') : '-'}
        </span>
      </div>
      <div className="flex w-full items-center justify-center px-2 py-4">
        <span className="w-full rounded-t-[4px] bg-gray-50 px-3 py-[10px] text-sm font-[450] text-gray-700">
          {data?.capitalOwned ? `${toRound(data.capitalOwned * 100, 2)}%` : '-'}
        </span>
      </div>
      <div className="flex w-full items-center justify-center px-2 py-4">
        <span className="w-full rounded-t-[4px] bg-gray-50 px-3 py-[10px] text-sm font-[450] text-gray-700">
          {data?.postRoundCapitalOwned ? `${toRound(data.postRoundCapitalOwned * 100, 2)}%` : '-'}
        </span>
      </div>
      <div className={twMerge('flex h-full items-center justify-center', pointerBlockedStyles)}>
        <Button
          className="w flex h-full w-full items-center justify-center"
          onClick={handleRemoveItem}
          styleType="NONE"
        >
          <CloseIcon />
        </Button>
      </div>
    </>
  );
};

export const CustomSafeItems: FC<NewItemListProps> = ({
  control,
  companyId,
  columns,
  addButtonText,
  scenarioId,
}) => {
  const { sharePrice } = useScenarioStore();
  const [isPending, setIsPending] = useState<boolean>(false);
  const { customSafes } = useWatch<FundraisingFormSchema>({ control });
  const { success: safeSuccess } = customSafesFormSchema
    .superRefine((data, ctx) => {
      data.safeItems.forEach((item, index) => {
        if (item.investment < sharePrice) {
          ctx.addIssue({
            code: 'custom',
            message: 'Investment must be more or equal share price',
            path: ['safeItems', index, 'investment'],
          });
        }
      });
    })
    .safeParse(customSafes);
  const skipUpdateIdsRef = useRef<Set<string>>(new Set());
  const isValidCustomSafes = (index: number) => {
    const cap = Number(customSafes?.safeItems?.[index]?.cap);
    const discount = Number(customSafes?.safeItems?.[index]?.discount);

    if (!cap && !discount) return false;

    if (cap === defaultNumericValue && discount === defaultNumericValue) return true;

    if (
      (cap && Number(cap) > defaultNumericValue) ||
      (discount && Number(discount) > defaultNumericValue)
    )
      return true;

    return false;
  };

  const {
    fields: itemsFields,
    append: itemsAppend,
    remove: itemsRemove,
  } = useFieldArray({
    control,
    name: 'customSafes.safeItems',
  });

  const handleRemoveItems = useCallback(
    (index: number) => {
      itemsRemove(index);
    },
    [itemsRemove],
  );

  const handleAppendItems = useCallback(() => {
    itemsAppend({
      investment: 0,
      cap: defaultNumericValue,
      capType: null,
      floor: 0,
      discount: defaultNumericValue,
      shareClass: {
        name: '',
        id: '',
      },
      stakeholder: {
        fullName: '',
        id: '',
      },
    });
  }, [itemsAppend]);

  return (
    <div
      className={twMerge(
        'relative flex w-full min-w-[1520px] flex-col gap-3',
        isPending ? 'pointer-events-none opacity-50' : '',
      )}
    >
      {isPending && <Loader className="absolute z-10" />}
      {itemsFields.length > 0 && (
        <div className="flex min-w-[1520px] flex-col rounded-lg border-[1px] border-gray-100 bg-white">
          <div className="grid" style={{ gridTemplateColumns: customSafeGridTemplateColumns }}>
            {columns.map((column, index) => (
              <TableHeaderItem key={index} text={column} />
            ))}
            <TableHeaderItem text="" />
          </div>
          {itemsFields.map((field, index) => (
            <Fragment key={field.id}>
              <div
                className="grid items-start border-b-[1px] border-b-gray-100"
                key={field.id}
                style={{ gridTemplateColumns: customSafeGridTemplateColumns }}
              >
                <CustomSafeItem
                  companyId={companyId}
                  control={control}
                  index={index}
                  onRemoveItem={() => handleRemoveItems(index)}
                  scenarioId={scenarioId}
                  setIsPending={setIsPending}
                  sharePrice={sharePrice}
                  skipUpdateIdsRef={skipUpdateIdsRef}
                />
              </div>
              {!isValidCustomSafes(index) && (
                <div className="flex items-center gap-1">
                  <InfoCircle />
                  <div className="whitespace-pre-line text-left font-inter text-xs font-[450] text-fireside-600">
                    <AppFormattedMessage id="CAP_OR_DISCOUNT_ENABLED" />
                  </div>
                </div>
              )}
            </Fragment>
          ))}
        </div>
      )}

      <div className="w-full bg-white">
        <Button
          className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent disabled:ring-0"
          disabled={itemsFields?.length > 0 && !safeSuccess}
          onClick={handleAppendItems}
          styleType="NONE"
        >
          + <AppFormattedMessage id="ADD" /> {addButtonText}
        </Button>
      </div>
    </div>
  );
};
